import { useState } from 'react';
import { X, Loader2, MapPin, Check, Copy } from 'lucide-react';
import { createVenue } from '@/api/venues';
import { copyFromVenue, type CopyPart } from '@/api/venueSetup';
import type { Restaurant, PlanTier } from '@gatheredhq/types';
import { meetsMinPlan } from '@gatheredhq/types';

const VENUE_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'bar', label: 'Bar' },
  { value: 'night_club', label: 'Night Club' },
  { value: 'food_truck', label: 'Food Truck' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'other', label: 'Other' },
];

const SERVICE_MODES = [
  { value: 'self_service', label: 'Self Service', desc: 'Counter ordering, QR codes', minPlan: 'menu' },
  { value: 'waiter', label: 'Table Service', desc: 'Table tracking & floor plans', minPlan: 'operate' },
  { value: 'hybrid', label: 'Hybrid', desc: 'Both table and counter service', minPlan: 'operate' },
] as const;

const EMIRATES = [
  'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah',
];

interface AddLocationModalProps {
  /** Venues that can be copied FROM — the org's existing ones. */
  existingVenues?: Restaurant[];
  plan?: string;
  onClose: () => void;
  onSuccess: (venue: Restaurant) => void;
}

export default function AddLocationModal({ plan = 'free', existingVenues = [], onClose, onSuccess }: AddLocationModalProps) {
  const [step, setStep] = useState<'form' | 'copy' | 'success'>('form');
  const [copySource, setCopySource] = useState('');
  const [copyParts, setCopyParts] = useState<CopyPart[]>(['menu', 'settings']);
  const [copying, setCopying] = useState(false);
  const [copySummary, setCopySummary] = useState<string | null>(null);
  const [createdVenue, setCreatedVenue] = useState<Restaurant | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    venueType: 'restaurant',
    serviceMode: 'self_service' as 'waiter' | 'self_service' | 'hybrid',
    emirate: '',
    address: '',
    phone: '',
  });

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Venue name is required'); return; }
    if (!form.emirate) { setError('Please select an emirate'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const venue = await createVenue({
        name: form.name.trim(),
        venueType: form.venueType,
        serviceMode: form.serviceMode,
        emirate: form.emirate || null,
        address: form.address || null,
        phone: form.phone || null,
      });
      setCreatedVenue(venue);
      // Offer the copy HERE, the moment the owner is looking at an empty venue — not on a
      // settings page they would have to find later.
      setStep(existingVenues.length > 0 ? 'copy' : 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create venue');
    } finally {
      setSubmitting(false);
    }
  };

  const runCopy = async () => {
    if (!createdVenue || !copySource || copyParts.length === 0) return;
    setCopying(true);
    setError(null);
    try {
      const result = await copyFromVenue(createdVenue.slug, copySource, copyParts);
      const bits: string[] = [];
      if (result.counts.items > 0) bits.push(`${result.counts.items} items`);
      if (result.counts.menus > 0) bits.push(`${result.counts.menus} menu${result.counts.menus === 1 ? '' : 's'}`);
      if (result.settingsCopied.length > 0) bits.push('settings');
      setCopySummary(bits.length > 0 ? `Copied ${bits.join(', ')}.` : 'Nothing to copy from that venue.');
      setStep('success');
    } catch (err) {
      // Stay on this step: the venue exists either way, and the owner can retry or skip
      // without losing it.
      setError(err instanceof Error ? err.message : 'Failed to copy');
    } finally {
      setCopying(false);
    }
  };

  if (step === 'copy' && createdVenue) {
    const togglePart = (part: CopyPart) =>
      setCopyParts(prev => prev.includes(part) ? prev.filter(p => p !== part) : [...prev, part]);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
        <div className="bg-surface border border-border rounded-[28px] w-full max-w-md shadow-2xl">
          <div className="p-8">
            <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-5">
              <Copy className="w-7 h-7 text-orange-500" />
            </div>
            <h2 className="text-xl font-black text-ink mb-1.5 tracking-tight">
              Start from an existing venue?
            </h2>
            <p className="text-ink-muted text-sm mb-6 leading-relaxed">
              {createdVenue.name} is empty. Copy a menu across and edit its prices and
              availability afterwards — each venue keeps its own.
            </p>

            <label className="block text-[11px] font-bold text-ink-muted uppercase tracking-wider mb-2">
              Copy from
            </label>
            <select
              value={copySource}
              onChange={e => setCopySource(e.target.value)}
              className="w-full bg-surface-muted border border-border text-ink h-11 rounded-xl px-4 text-sm mb-4"
            >
              <option value="">Choose a venue…</option>
              {existingVenues
                .filter(v => v.slug !== createdVenue.slug)
                .map(v => <option key={v.id} value={v.slug}>{v.name}</option>)}
            </select>

            <div className="space-y-2 mb-6">
              {([
                { id: 'menu' as const, label: 'Menus, items and add-ons', help: 'Including every translation you have already done.' },
                { id: 'settings' as const, label: 'Settings', help: 'Hours, languages, currencies, tips. Not the address or phone.' },
              ]).map(part => (
                <label key={part.id} className="flex items-start gap-3 p-3 rounded-xl border border-border cursor-pointer hover:border-orange-500/40 transition-colors">
                  <input
                    type="checkbox"
                    checked={copyParts.includes(part.id)}
                    onChange={() => togglePart(part.id)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">{part.label}</span>
                    <span className="block text-[11px] text-ink-muted mt-0.5">{part.help}</span>
                  </span>
                </label>
              ))}
            </div>

            {error && (
              <p className="text-red-500 text-xs mb-4">{error}</p>
            )}

            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={() => setStep('success')}
                className="flex-1 h-11 rounded-xl border border-border text-sm font-semibold text-ink"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => void runCopy()}
                disabled={!copySource || copyParts.length === 0 || copying}
                className="btn-gradient flex-1 h-11 rounded-xl text-sm font-bold text-white disabled:opacity-50"
              >
                {copying ? 'Copying…' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success' && createdVenue) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
        <div className="bg-surface border border-border rounded-[28px] w-full max-w-md shadow-2xl">
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-500/10 rounded-[28px] flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-black text-ink mb-2 tracking-tight">Location Added!</h2>
            <p className="text-ink-muted mb-6 font-medium">
              {copySummary ?? `${createdVenue.name} is ready. Set up your menu and settings.`}
            </p>
            <button
              onClick={() => { onSuccess(createdVenue); onClose(); }}
              className="w-full btn-gradient py-4 font-bold active:scale-[0.98] transition-all"
            >
              Go to {createdVenue.name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="bg-surface border border-border rounded-[28px] w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl scrollbar-premium">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-surface/80 backdrop-blur-xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-orange-500" />
            </div>
            <h2 className="text-lg font-black text-ink tracking-tight">Add New Location</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-2 rounded-xl hover:bg-surface-muted transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={e => void handleSubmit(e)} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-500 rounded-2xl px-4 py-3 text-sm font-medium">
              {error}
            </div>
          )}

          {/* Venue Name */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">
              <span className="text-orange-500">*</span> Venue Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="e.g. Salt JBR"
              className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-ink placeholder-ink-faint focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
            />
          </div>

          {/* Venue Type */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">Venue Type</label>
            <div className="grid grid-cols-4 gap-2">
              {VENUE_TYPES.map(vt => (
                <button
                  key={vt.value}
                  type="button"
                  onClick={() => handleChange('venueType', vt.value)}
                  className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                    form.venueType === vt.value
                      ? 'bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'bg-surface border-border text-ink-muted hover:border-ink-faint'
                  }`}
                >
                  {vt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Service Mode */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">Service Mode</label>
            <div className="grid grid-cols-3 gap-2">
              {SERVICE_MODES.map(sm => {
                // The shared ladder. The local copy had no `hotel` and no `starter`, so
                // a Hotel org scored 0 and every service mode here looked locked.
                const locked = !meetsMinPlan(plan as PlanTier, sm.minPlan as PlanTier);
                return (
                  <button
                    key={sm.value}
                    type="button"
                    onClick={() => !locked && handleChange('serviceMode', sm.value)}
                    disabled={locked}
                    className={`flex flex-col items-center p-3 rounded-xl border transition-all ${
                      locked
                        ? 'bg-surface-muted/50 border-border text-ink-faint cursor-not-allowed opacity-50'
                        : form.serviceMode === sm.value
                          ? 'bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400'
                          : 'bg-surface border-border text-ink-muted hover:border-ink-faint'
                    }`}
                  >
                    <span className="text-xs font-bold">{sm.label}</span>
                    <span className="text-[9px] mt-1 opacity-60">{sm.desc}</span>
                    {locked && <span className="text-[8px] mt-1 text-orange-500 font-bold">Operate+</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Emirate */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">
              <span className="text-orange-500">*</span> Emirate
            </label>
            <select
              value={form.emirate}
              onChange={e => handleChange('emirate', e.target.value)}
              className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-ink appearance-none focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
            >
              <option value="">Select emirate...</option>
              {EMIRATES.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {/* Address */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">Address</label>
            <input
              type="text"
              value={form.address}
              onChange={e => handleChange('address', e.target.value)}
              placeholder="Street, building, area"
              className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-ink placeholder-ink-faint focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-2">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => handleChange('phone', e.target.value)}
              placeholder="+971 50 123 4567"
              className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-ink placeholder-ink-faint focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-gradient py-4 font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 mt-2"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Location'}
          </button>
        </form>
      </div>
    </div>
  );
}
